import { motion } from "framer-motion";
import { Link } from "../router";
import type { MarkdownPost } from "../utils/data";
import { Calendar, Clock, Layers } from "lucide-react";
import { ALL_ICONS } from "./Icons";

interface BlogCardProps {
    post: MarkdownPost;
    index: number;
}

export default function BlogCard({ post, index }: BlogCardProps) {
    const { slug } = post;
    const { title, excerpt, date, readTime, tag, icon } = post.frontmatter;
    const IconComponent = (icon && ALL_ICONS[icon]) || Layers;

    return (
        <Link to={`/blog/${slug}`} className="block w-full">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="group relative flex flex-row bg-[#0F0F0F] border-2 border-[#1F1F1F] rounded-2xl cursor-pointer transition-colors duration-200 hover:border-[#333] hover:bg-[#121212] overflow-hidden p-3 md:p-4"
            >
                {/* 1. LEFT COLUMN: Icon Box (Scaled Down & Equidistant) */}
                <div className="shrink-0">
                    <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[var(--accent)] group-hover:bg-[#222] group-hover:border-[#333] transition-all duration-300 shadow-inner">
                        <IconComponent className="w-6 h-6 md:w-9 md:h-9" />
                    </div>
                </div>

                {/* 2. RIGHT COLUMN: Content (Aligned Top/Bottom with Logo) */}
                <div className="flex flex-col flex-grow ml-4 justify-between relative h-14 md:h-20 py-0.5 md:py-1">

                    {/* TOP ROW: Title & Tag (Aligned with Top of Logo) */}
                    <div className="flex justify-between items-start leading-none">
                        <div className="flex items-center gap-2 md:gap-3">
                            <h3 className="text-base md:text-xl font-bold text-white group-hover:text-[var(--accent)] transition-colors duration-200 line-clamp-1">
                                {title}
                            </h3>

                            {/* Dot Separator (Outside Tag) */}
                            <span className="hidden md:block w-1 h-1 rounded-full bg-[#444] shrink-0" />

                            {/* Desktop Tag */}
                            <span className="hidden md:block px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-[#151515] border border-[#333] rounded-md group-hover:border-[#444] transition-colors whitespace-nowrap">
                                {tag}
                            </span>
                        </div>

                        {/* Meta Badge */}
                        <div className="flex items-center gap-2 shrink-0 text-[9px] md:text-[10px] font-mono text-[var(--text-muted)] bg-[#1A1A1A] px-2 py-1 rounded-md border border-[#2A2A2A] transition-colors group-hover:border-[#444]">
                            <div className="flex items-center gap-1.5 border-r border-[#333] pr-2">
                                <Clock className="w-3 h-3" />
                                <span>{readTime}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" />
                                <span>{date}</span>
                            </div>
                        </div>
                    </div>

                    {/* MIDDLE: Mobile Tag / Excerpt */}
                    {/* We hide excerpt on very small screens if needed, or keep line-clamp-1 */}

                    {/* BOTTOM ROW: Excerpt & Read More (Aligned with Bottom of Logo) */}
                    <div className="flex justify-between items-end">
                        <p className="text-[11px] md:text-xs text-[#888] font-medium leading-relaxed line-clamp-1 opacity-80 max-w-[70%]">
                            {excerpt}
                        </p>

                        <span className="text-[10px] md:text-[11px] font-bold text-[var(--text-muted)] group-hover:text-white group-hover:underline underline-offset-4 decoration-[var(--accent)] transition-all duration-200 whitespace-nowrap">
                            Devamını Oku
                        </span>
                    </div>
                </div>

            </motion.div>
        </Link>
    );
}
